<template>
  <v-container fluid class="edit-page">
    <!-- Edit Application Confirm Dialog -->
    <v-dialog v-model="alertEditApplicationDialog" max-width="460" content-class="edit-confirm-dialog">
      <div class="edit-confirm-card">
        <h3 class="edit-confirm-card__title">Edit Application</h3>
        <p class="edit-confirm-card__message">Are you sure to edit this application?</p>
        <div class="edit-confirm-card__actions">
          <button
            class="edit-confirm-card__btn edit-confirm-card__btn--cancel"
            @click="alertEditApplicationDialog = false"
          >
            Cancel
          </button>
          <button
            class="edit-confirm-card__btn edit-confirm-card__btn--confirm"
            @click="editApplication()"
          >
            Update
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- Header -->
    <div class="edit-header">
      <div class="edit-header__top">

        <div v-if="!loadingDialog" class="edit-header__info">
          <h1 class="edit-header__title">{{ model.edit.name }}</h1>
        </div>

        <div class="edit-header__actions">
          <span v-if="this.isHrdcSuperAdminRoles">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <button
                  class="hero-action-btn hero-action-btn--dark"
                  @click.stop="alertEditApplicationDialog = true"
                  v-bind="attrs"
                  v-on="on"
                >
                  <!-- Heroicon: floppy-disk / save -->
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="hero-icon">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M5.25 3.75h10.586a1.5 1.5 0 0 1 1.06.44l2.914 2.914a1.5 1.5 0 0 1 .44 1.06V19.5a1.5 1.5 0 0 1-1.5 1.5h-13.5a1.5 1.5 0 0 1-1.5-1.5v-15a1.5 1.5 0 0 1 1.5-1.5Z" />
                    <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 21v-4.5a1.5 1.5 0 0 0-1.5-1.5h-4.5a1.5 1.5 0 0 0-1.5 1.5V21" />
                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 3.75v3h6v-3" />
                  </svg>
                </button>
              </template>
              <span>Save Changes</span>
            </v-tooltip>
          </span>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div v-if="!loadingDialog" class="cards-grid">
      <!-- LEFT COLUMN -->
      <div class="cards-column">
        <!-- Company Details -->
        <div class="section-card">
          <div class="section-header">
            <v-icon color="white" small>mdi-office-building-outline</v-icon>
            <span class="section-header__title">Company Details</span>
          </div>
          <div class="section-body">
            <div class="form-row">
              <label class="form-label">Company Name</label>
              <div class="form-field">
                <v-text-field
                  filled
                  dense
                  v-model="companyDetails.companyName"
                  :error-messages="errorField.companyName"
                ></v-text-field>
              </div>
            </div>

            <div class="form-row">
              <label class="form-label">Company SSM No.</label>
              <div class="form-field">
                <v-text-field
                  filled
                  dense
                  v-model="companyDetails.companySsmNo"
                  :error-messages="errorField.companySsmNo"
                ></v-text-field>
              </div>
            </div>

            <div class="form-row">
              <label class="form-label">Company TIN No.</label>
              <div class="form-field">
                <v-text-field
                  filled
                  dense
                  v-model="companyDetails.companyTinNo"
                  :error-messages="errorField.companyTinNo"
                ></v-text-field>
              </div>
            </div>

            <div class="form-row">
              <label class="form-label">Company Address</label>
              <div class="form-field">
                <v-text-field
                  filled
                  dense
                  v-model="companyDetails.companyAddress"
                  :error-messages="errorField.companyAddress"
                ></v-text-field>
              </div>
            </div>

            <div class="section-divider"></div>

            <div class="form-row">
              <label class="form-label">Client PIC Name</label>
              <div class="form-field">
                <v-combobox
                  filled
                  dense
                  multiple
                  small-chips
                  :items="companyDetails.picNameItems"
                  v-model="companyDetails.picName"
                  :error-messages="errorField.picName"
                >
                  <template v-slot:selection="data">
                    <v-chip
                      small
                      :key="JSON.stringify(data.item)"
                      v-bind="data.attrs"
                      :input-value="data.selected"
                      :disabled="data.disabled"
                      @click:close="data.parent.selectItem(data.item)"
                    >
                      {{ data.item }}
                      <v-icon
                        small
                        @click="data.parent.selectItem(data.item)"
                      >
                        $delete
                      </v-icon>
                    </v-chip>
                  </template>
                  <template v-slot:append>
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-icon v-on="on"> mdi-information </v-icon>
                      </template>
                      Press <kbd>enter/tab</kbd> to insert multiple
                      email
                    </v-tooltip>
                  </template>
                </v-combobox>
              </div>
            </div>

            <div class="form-row">
              <label class="form-label">Client PIC Email</label>
              <div class="form-field">
                <v-combobox
                  filled
                  dense
                  multiple
                  small-chips
                  :items="companyDetails.picEmailItems"
                  v-model="companyDetails.picEmail"
                  :error-messages="errorField.picEmail"
                >
                  <template v-slot:selection="data">
                    <v-chip
                      small
                      :key="JSON.stringify(data.item)"
                      v-bind="data.attrs"
                      :input-value="data.selected"
                      :disabled="data.disabled"
                      @click:close="data.parent.selectItem(data.item)"
                    >
                      {{ data.item }}
                      <v-icon
                        small
                        @click="data.parent.selectItem(data.item)"
                      >
                        $delete
                      </v-icon>
                    </v-chip>
                  </template>
                  <template v-slot:append>
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-icon v-on="on"> mdi-information </v-icon>
                      </template>
                      Press <kbd>enter/tab</kbd> to insert multiple
                      email
                    </v-tooltip>
                  </template>
                </v-combobox>
              </div>
            </div>

            <div class="form-row">
              <label class="form-label">Client PIC Contact No</label>
              <div class="form-field">
                <v-combobox
                  filled
                  dense
                  multiple
                  small-chips
                  :items="companyDetails.picContactNoItems"
                  v-model="companyDetails.picContactNo"
                  :error-messages="errorField.picContactNo"
                >
                  <template v-slot:selection="data">
                    <v-chip
                      small
                      :key="JSON.stringify(data.item)"
                      v-bind="data.attrs"
                      :input-value="data.selected"
                      :disabled="data.disabled"
                      @click:close="data.parent.selectItem(data.item)"
                    >
                      {{ data.item }}
                      <v-icon
                        small
                        @click="data.parent.selectItem(data.item)"
                      >
                        $delete
                      </v-icon>
                    </v-chip>
                  </template>
                  <template v-slot:append>
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-icon v-on="on"> mdi-information </v-icon>
                      </template>
                      Press <kbd>enter/tab</kbd> to insert multiple
                      email
                    </v-tooltip>
                  </template>
                </v-combobox>
              </div>
            </div>

            <div class="section-divider"></div>

            <div class="form-row">
              <label class="form-label">Bank Name</label>
              <div class="form-field">
                <v-text-field
                  filled
                  dense
                  v-model="companyDetails.bankUuid"
                  :error-messages="errorField.bankUuid"
                  :disabled="paymentChannelDisabled"
                ></v-text-field>
              </div>
            </div>

            <div class="form-row">
              <label class="form-label">Bank Account No</label>
              <div class="form-field">
                <v-text-field
                  filled
                  dense
                  v-model="companyDetails.bankAccNo"
                  :error-messages="errorField.bankAccNo"
                  :disabled="paymentChannelDisabled"
                ></v-text-field>
              </div>
            </div>

            <div class="form-row">
              <label class="form-label">Bank Account Holder Name</label>
              <div class="form-field">
                <v-text-field
                  filled
                  dense
                  v-model="companyDetails.bankAccHolderName"
                  :error-messages="errorField.bankAccHolderName"
                  :disabled="paymentChannelDisabled"
                ></v-text-field>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- RIGHT COLUMN -->
      <div class="cards-column">
        <!-- Application Details -->
        <div class="section-card">
          <div class="section-header">
            <v-icon color="white" small>mdi-application-edit-outline</v-icon>
            <span class="section-header__title">Application Details</span>
          </div>
          <div class="section-body">
            <div class="form-row">
              <label class="form-label">Programme Name</label>
              <div class="form-field">
                <v-text-field
                  v-model="applicationDetails.programmeName"
                  :error-messages="errorField.programmeName"
                  filled
                  dense
                ></v-text-field>
              </div>
            </div>

            <div class="form-row">
              <label class="form-label">Claim Amount</label>
              <div class="form-field">
                <v-text-field
                  v-model="applicationDetails.totalPrice"
                  :error-messages="errorField.totalPrice"
                  filled
                  dense
                  hide-spin-buttons
                  type="number"
                  @wheel="$event.target.blur()"
                  @keydown.up.prevent
                  @keydown.down.prevent
                  label="etc: 2600, 1800, ..."
                ></v-text-field>
              </div>
            </div>

            <div class="form-row">
              <label class="form-label">Payment Amount</label>
              <div class="form-field">
                <v-text-field
                  v-model="applicationDetails.paymentAmount"
                  :error-messages="errorField.paymentAmount"
                  :disabled="paymentChannelDisabled"
                  filled
                  dense
                  hide-spin-buttons
                  type="number"
                  @wheel="$event.target.blur()"
                  @keydown.up.prevent
                  @keydown.down.prevent
                  label="etc: 2600, 1800, ..."
                ></v-text-field>
              </div>
            </div>

            <div class="form-row">
              <label class="form-label">Payment Channel</label>
              <div class="form-field">
                <v-select
                  @change="changePaymentChannel()"
                  v-model="applicationDetails.paymentChannel"
                  :items="[
                    {
                      text: 'Pay & Refund',
                      value: 1,
                    },
                    {
                      text: 'Claim Basis',
                      value: 2,
                    },
                  ]"
                  dense
                  filled
                ></v-select>
              </div>
            </div>

            <div class="form-row" v-if="!paymentChannelDisabled">
              <label class="form-label">Payment Mode</label>
              <div class="form-field">
                <v-select
                  v-model="applicationDetails.paymentMode"
                  :error-messages="errorField.paymentMode"
                  :items="paymentMethodItems"
                  dense
                  filled
                ></v-select>
              </div>
            </div>

            <div
              class="form-row"
              v-if="
                !paymentChannelDisabled &&
                applicationDetails.paymentMode == 'Other'
              "
            >
              <label class="form-label">Other Payment Mode</label>
              <div class="form-field">
                <v-text-field
                  v-model="applicationDetails.otherPaymentMode"
                  :error-messages="errorField.otherPaymentMode"
                  dense
                  filled
                ></v-text-field>
              </div>
            </div>

            <div class="form-row">
              <label class="form-label">Screenshot of Bank Account for Refund</label>
              <div class="form-field">
                <div
                  v-cloak
                  @drop.prevent="addDropFile($event, 'ssOfBankAcc')"
                  @dragover.prevent
                  class="w-100"
                >
                  <v-file-input
                    :prepend-icon="null"
                    prepend-inner-icon="mdi-paperclip"
                    dense
                    filled
                    small-chips
                    v-model="applicationDetails.ssOfBankAcc"
                    :error-messages="errorField.ssOfBankAcc"
                    :truncate-length="1000"
                    :disabled="paymentChannelDisabled"
                  ></v-file-input>
                </div>
              </div>
            </div>

            <div class="form-row">
              <label class="form-label">Event</label>
              <div class="form-field">
                <v-autocomplete
                  @change="changePaymentChannel()"
                  v-model="applicationDetails.eventUuid"
                  :items="eventItems"
                  :loading="apiGetEvents.isLoading"
                  dense
                  filled
                ></v-autocomplete>
              </div>
            </div>

            <div class="form-row">
              <label class="form-label">Is Application Duplicate</label>
              <div class="form-field">
                <v-select
                  @change="changePaymentChannel()"
                  v-model="applicationDetails.isApplicationDuplicate"
                  :items="[
                    {
                      text: 'Yes',
                      value: true,
                    },
                    {
                      text: 'No',
                      value: false,
                    },
                  ]"
                  dense
                  filled
                ></v-select>
              </div>
            </div>

            <div class="section-divider"></div>

            <div class="form-row">
              <label class="form-label">No. of Pax</label>
              <div class="form-field">
                <v-text-field
                  v-model="applicationDetails.noOfPax"
                  :error-messages="errorField.noOfPax"
                  filled
                  dense
                  hide-spin-buttons
                  type="number"
                  @wheel="$event.target.blur()"
                  @keydown.up.prevent
                  @keydown.down.prevent
                  label="Maximum 10 Pax"
                >
                  <template v-slot:append>
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-icon v-on="on"> mdi-information </v-icon>
                      </template>
                      Change the number of pax to add more participants'
                      details
                    </v-tooltip>
                  </template>
                </v-text-field>
              </div>
            </div>

            <div
              class="form-row form-row--participants"
              v-for="(pax, index) in Number(applicationDetails.noOfPax)"
              :key="index"
            >
              <label class="form-label">Participant {{ pax }} Details</label>
              <div class="form-field form-field--participants">
                <v-text-field
                  ref="participantName"
                  v-model="applicationDetails.participantName[index]"
                  :error-messages="errorField.noOfPax"
                  filled
                  dense
                  :label="`Participant ${pax} Full Name`"
                  class="participant-input"
                ></v-text-field>
                <v-text-field
                  ref="participantNric"
                  v-model="applicationDetails.participantNric[index]"
                  :error-messages="errorField.noOfPax"
                  filled
                  dense
                  :label="`Participant ${pax} NRIC`"
                  class="participant-input"
                ></v-text-field>
                <v-text-field
                  ref="participantEmail"
                  v-model="applicationDetails.participantEmail[index]"
                  :error-messages="errorField.noOfPax"
                  filled
                  dense
                  :label="`Participant ${pax} Email`"
                  class="participant-input"
                ></v-text-field>
              </div>
            </div>

            <div class="section-divider"></div>

            <div class="form-row">
              <label class="form-label">Responder Email</label>
              <div class="form-field">
                <v-autocomplete
                  v-model="applicationDetails.referByEmail"
                  :items="csDetails.salespersonData"
                  dense
                  filled
                  label="Responder Email"
                  @change="appendSalespersonName()"
                ></v-autocomplete>
              </div>
            </div>

            <div class="form-row">
              <label class="form-label">Responder Name</label>
              <div class="form-field">
                <v-text-field
                  v-model="applicationDetails.referByName"
                  :error-messages="errorField.referByName"
                  filled
                  dense
                  disabled
                  label="Responder Name"
                ></v-text-field>
              </div>
            </div>
          </div>
        </div>

        <!-- Customer Service Details -->
        <div class="section-card">
          <div class="section-header">
            <v-icon color="white" small>mdi-face-agent</v-icon>
            <span class="section-header__title">Customer Service Details</span>
          </div>
          <div class="section-body">
            <div class="form-row">
              <label class="form-label">Customer Service</label>
              <div class="form-field">
                <v-autocomplete
                  dense
                  filled
                  label="Customer Service"
                  v-model="csDetails.customerService"
                  :error-messages="errorField.customerService"
                  :items="csDetails.customerServiceData"
                  multiple
                  small-chips
                >
                  <template v-slot:selection="data">
                    <v-chip
                      small
                      :key="JSON.stringify(data.item)"
                      v-bind="data.attrs"
                      :input-value="data.selected"
                      :disabled="data.disabled"
                      @click:close="data.parent.selectItem(data.item)"
                    >
                      {{ data.item.text }}
                      <v-icon
                        small
                        @click="data.parent.selectItem(data.item)"
                      >
                        $delete
                      </v-icon>
                    </v-chip>
                  </template>

                  <template v-slot:append>
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-icon v-on="on"> mdi-information </v-icon>
                      </template>
                      Press <kbd>enter/tab</kbd> to insert multiple
                      email
                    </v-tooltip>
                  </template>
                </v-autocomplete>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Page Loading State -->
    <div v-if="loadingDialog" class="page-loader">
      <div class="page-loader__skeleton">
        <div class="skel-header">
          <div class="skel-line skel-line--lg"></div>
          <div class="skel-line skel-line--sm"></div>
        </div>
        <div class="skel-grid">
          <div class="skel-card" v-for="n in 4" :key="n">
            <div class="skel-card__header"></div>
            <div class="skel-card__body">
              <div class="skel-line skel-line--md"></div>
              <div class="skel-line skel-line--full"></div>
              <div class="skel-line skel-line--lg"></div>
              <div class="skel-line skel-line--md"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <HrdcLoader :loadingDialog="loadingDialog"></HrdcLoader>

    <!-- Loading Bar Dialog -->
    <v-dialog v-model="loadingBar" persistent width="300">
      <v-card color="primary" dark>
        <v-card-text>
          Loading
          <v-progress-linear
            indeterminate
            color="white"
            class="mb-0"
          ></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapState } from "vuex";
import Api from "@/objects/api";
import Model from "@/objects/model";
import Service from "@/objects/service";
import hrdcFunction from "@/objects/hrdcFunction";
import HrdcData from "@/services/hrdc_event/objects/globalData";

export default {
  mixins: [hrdcFunction],
  computed: {
    ...mapState({
      auth: (state) => state.auth.data,
    }),
    paymentChannelDisabled() {
      let disabled = false;
      if (this.applicationDetails.paymentChannel == 2) {
        disabled = true;
      }

      return disabled;
    },
  },
  props: ["params"],
  data: () => ({
    model: new Model(),
    service: new Service(),
    hrdcData: new HrdcData(),
    apiEditApplication: new Api(),
    apiGetApplicationDetails: new Api(),
    apiGetCsAndSalespersonList: new Api(),
    apiGetEvents: new Api(),
    allStages: [],
    loadingDialog: false,
    loadingBar: false,
    panel: [0, 1, 2, 3, 4, 5, 6, 7],
    companyDetails: {
      companyUuid: null,
      companyName: null,
      companySsmNo: null,
      companyTinNo: null,
      companyAddress: null,
      picName: [],
      picNameItems: [],
      picEmail: [],
      picEmailItems: [],
      picContactNo: [],
      picContactNoItems: [],
      bankUuid: null,
      bankAccNo: null,
      bankAccHolderName: null,
      totalPrice: null,
      oriBankUuid: null,
      oriBankAccNo: null,
      oriBankAccHolderName: null,
    },
    errorField: {
      companyUuid: null,
      companyName: null,
      companySsmNo: null,
      companyTinNo: null,
      companyAddress: null,
      picName: null,
      picContactNo: null,
      bankUuid: null,
      bankAccNo: null,
      bankAccHolderName: null,
      customerService: null,
      totalPrice: null,
      programmeName: null,
      paymentMode: null,
      referByName: null,
      referByEmail: null,
      eventUuid: null,
    },
    salespersonDetails: {
      salespersonName: null,
      salespersonEmail: null,
      salespersonContactNo: null,
    },
    csDetails: {
      customerService: null,
      customerServiceData: [],
      salespersonData: [],
    },
    applicationDetails: {
      paymentChannel: null,
      ssOfBankAcc: null,
      oriSsOfBankAcc: null,
      programmeName: null,
      paymentMode: null,
      otherPaymentMode: null,
      referByName: null,
      referByEmail: null,
      paymentAmount: null,
      noOfPax: null,
      isApplicationDuplicate: null,
      participantName: Array(10).fill(""),
      participantNric: Array(10).fill(""),
      participantEmail: Array(10).fill(""),
      eventUuid: null,
    },
    alertEditApplicationDialog: false,
    isPaymentMade: null,
    salespersonData: [],
    incomingData: {},
    eventItems: [],
  }),
  watch: {
    "applicationDetails.noOfPax"(newValue) {
      if (newValue > 10) {
        this.applicationDetails.noOfPax = null;
      }
    },
  },
  async created() {
    this.showLoadingDialog();
    this.model.getByKey(this.params.modelKey);
    if (this.model.serviceKey) this.service.set("key", this.model.serviceKey);
    this.allStages = await this.hrdcData.allStages;
    this.rolesAssignation(this.auth.roles);
    this.serviceDataAssignation(this.model.serviceKey);

    // Assign apiDetails by mapping repository.
    const apiDetails = {
      apiGetApplicationDetails: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/application/get-application-details`,
        method: "post",
        params: {
          applicationUuid: this.$route.query._ausk,
        },
      },
      apiEditApplication: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/application/edit-application`,
        method: "post",
      },
      apiGetCsAndSalespersonList: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/application/get-cs-and-salesperson-list`,
        method: "post",
        params: {
          internalRoleSlugs: ["taxpod-customer-service"],
        },
      },
      apiGetEvents: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/settings/event/get-events?event_type=${this.hrdcType}`,
      },
    };

    for (const api in apiDetails) {
      if (apiDetails[api].url !== undefined) {
        this[api].setUrl(apiDetails[api].url);
      }
      if (apiDetails[api].method !== undefined) {
        this[api].setMethod(apiDetails[api].method);
      }
      if (apiDetails[api].params !== undefined) {
        this[api].setParams(apiDetails[api].params);
      }
      if (apiDetails[api].headers !== undefined) {
        this[api].setHeaders(apiDetails[api].headers);
      }
    }

    this.apiGetApplicationDetails.setCallbackCompleted((response) => {
      try {
        const { data } = response;

        if (!response.status) {
          this.$store.commit("assignDeveloperData", {
            paginationPageNo: this.$route.query.currentPageNo,
          });

          this.$router.push({
            name: this.eventRoute("Application"),
            query: {
              viewId: this.$store.state[this.service.key].data.viewId,
              tabIndex: 0,
            },
          });
          this.$store.dispatch("showMessage", {
            message: data,
            messageType: "error",
            timeout: 2000,
          });
          return;
        }

        // Store incoming request for Editted Data History purpose.
        this.storeDataHistory(data);

        // Company details.
        this.companyDetails = this.transformCompanyDetails(data, response);

        // Application Details.
        this.applicationDetails.paymentChannel = data.paymentChannel;
        this.applicationDetails.programmeName = data.programmeName;
        this.applicationDetails.paymentMode = data.paymentMode;
        this.applicationDetails.referByName = data.referByName;
        this.applicationDetails.referByEmail = data.referByEmail;
        this.applicationDetails.totalPrice = data.totalPrice;
        this.applicationDetails.paymentAmount = data.paymentAmount ?? 0;
        this.applicationDetails.noOfPax = data.noOfPax;
        this.applicationDetails.isApplicationDuplicate =
          data.isApplicationDuplicate;
        this.applicationDetails.eventUuid = data.eventUuid;

        // Assign event participants.
        for (let i = 0; i < data.ApplicationEventParticipant.length; i++) {
          this.applicationDetails.participantName[i] =
            data.ApplicationEventParticipant[i].participantName;
          this.applicationDetails.participantNric[i] =
            data.ApplicationEventParticipant[i].participantNric;
          this.applicationDetails.participantEmail[i] =
            data.ApplicationEventParticipant[i].participantEmail;
        }

        for (const item of response.attachments) {
          // Create a Blob object from the data
          const blob = new Blob([new Uint8Array(item.dataArray)]);

          // Create the File object
          const file = new File([blob], item.fileName, {
            type: `application/${item.extension}`,
          });

          if (item.type == "Screenshot of Bank Account Details for Refund") {
            this.applicationDetails.ssOfBankAcc = file;
            this.applicationDetails.oriSsOfBankAcc = file;
          }
        }

        // Salesperson details.
        this.salespersonDetails.salespersonName = data.salesperson.name ?? "-";
        this.salespersonDetails.salespersonEmail =
          data.salesperson.email ?? "-";
        this.salespersonDetails.salespersonContactNo =
          data.salesperson.mobile ?? "-";

        // Customer Service details.
        const csArrTmp = [];
        if (response.applicationCs.length > 0) {
          for (const customerService of response.applicationCs) {
            csArrTmp.push({
              text: customerService.csEmail,
              value: customerService.csUuid,
            });
          }
          this.csDetails.customerService = csArrTmp;
        }

        // Check whether company exists in vision or not.
        this.isPaymentMade = data.isPaymentMade;

        // Get CS Listing.
        this.apiGetCsAndSalespersonList.setCallbackCompleted((response) => {
          try {
            const { data, status, csData } = response;

            if (status) {
              // Handle if sso server down, just display empty items for cs lists.
              if (csData) {
                for (const item of csData) {
                  this.csDetails.customerServiceData.push({
                    text: item.email,
                    value: item.uuid,
                  });
                }
              }

              if (data) {
                this.salespersonData = data;
                for (const salespersonItem of data) {
                  this.csDetails.salespersonData.push(salespersonItem.email);
                }
              }
            }
            this.hideLoadingDialog();
          } catch (err) {
            console.log(err);
          }
        });
        this.apiGetCsAndSalespersonList.fetch();
      } catch (err) {
        console.log(err);
      }
    });
    this.apiGetApplicationDetails.fetch();

    // Get events data.
    this.apiGetEvents.setCallbackCompleted((response) => {
      try {
        const { status, data } = response;
        if (!status || data.length == 0) {
          this.errorField.eventUuid =
            "Unavailable to retrieve events, please contact admin for support";
        }

        if (status) {
          for (const item of data) {
            this.eventItems.push({
              text: this.formatEventTitle(item),
              value: item.uuid,
            });
          }
        }
      } catch (err) {
        this.errorField.eventUuid =
          "Unavailable to retrieve events, please contact admin for support";
      }
    });
    this.apiGetEvents.fetch();
  },
  methods: {
    isArrayofObjects(arr) {
      if (arr !== null) {
        return (
          arr.length > 0 &&
          typeof arr[0] === "object" &&
          arr[0] !== null &&
          !Array.isArray(arr[0])
        );
      }
    },
    changePaymentChannel() {
      this.clearErrorMsg();
      if (this.applicationDetails.paymentChannel == 1) {
        this.companyDetails.bankUuid = this.companyDetails.oriBankUuid;
        this.companyDetails.bankAccNo = this.companyDetails.oriBankAccNo;
        this.companyDetails.bankAccHolderName =
          this.companyDetails.oriBankAccHolderName;
        this.applicationDetails.ssOfBankAcc =
          this.applicationDetails.oriSsOfBankAcc;
      } else {
        this.companyDetails.bankUuid = null;
        this.companyDetails.bankAccNo = null;
        this.companyDetails.bankAccHolderName = null;
        this.applicationDetails.ssOfBankAcc = null;
      }
    },
    clearErrorMsg() {
      for (let error in this.errorField) {
        this.errorField[error] = null;
      }
    },
    showErrorMessage(response) {
      for (const key in response.data) {
        this.errorField[key] = response.data[key];
      }
    },
    // Create function to capture original company data.
    transformCompanyDetails(data, response) {
      return {
        companyUuid: data.Company[0].uuid,
        companyName: `${data.oriCompanyName}`,
        companySsmNo: data.Company[0].oldSsmNo,
        companyTinNo: data.Company[0].companyTinNo,
        companyAddress: data.Company[0].oldAddress,
        picName: response.companyPicData
          .map((item) => item.picName)
          .filter((item) => item !== null),
        picNameItems: response.companyPicData
          .map((item) => item.picName)
          .filter((item) => item !== null),
        picEmail: response.companyPicData
          .map((item) => item.picEmail)
          .filter((item) => item !== null),
        picEmailItems: response.companyPicData
          .map((item) => item.picEmail)
          .filter((item) => item !== null),
        picContactNo: response.companyPicData
          .map((item) => item.picContactNo)
          .filter((item) => item !== null),
        picContactNoItems: response.companyPicData
          .map((item) => item.picContactNo)
          .filter((item) => item !== null),
        bankUuid: data.bankUuid ?? null,
        bankAccNo:
          data.oldBankAccount == null || data.oldBankAccount == "null"
            ? null
            : data.oldBankAccount.replace(/\s+/g, ""),
        bankAccHolderName:
          data.oldBankAccountHolderName == null || data.oldBankAccount == "null"
            ? null
            : data.oldBankAccountHolderName,
        oriBankUuid: data.bankUuid ?? null,
        oriBankAccNo:
          data.oldBankAccount == null || data.oldBankAccount == "null"
            ? null
            : data.oldBankAccount.replace(/\s+/g, ""),
        oriBankAccHolderName:
          data.oldBankAccountHolderName == null || data.oldBankAccount == "null"
            ? null
            : data.oldBankAccountHolderName,
        totalPrice: data.totalPrice,
      };
    },
    checkRoleAllows(checkRole) {
      return this.auth.roles.some((role) => role.slug.includes(checkRole));
    },
    appendSalespersonName() {
      // Get the salesperson name.
      const salespersonObj = this.salespersonData.find(
        (item) => item.email == this.applicationDetails.referByEmail
      );

      this.applicationDetails.referByName = salespersonObj.name;
    },
    redirectBack() {
      let route = this.eventRoute("Application");
      if (this.params.backTo) {
        route = this.params.backTo;
      }

      this.$store.commit("assignDeveloperData", {
        paginationPageNo: this.$route.query.currentPageNo,
      });

      this.$router.push({
        name: route,
        params: {
          modelKey: this.model.key,
          backTo: "",
        },
        query: {
          viewId: this.$store.state[this.service.key].data.viewId,
          _ausk: this.$route.query._ausk,
        },
      });
    },
    checkEndingComma(str) {
      return str.endsWith(",") ? str.slice(0, -1) : str;
    },
    storeDataHistory(data) {
      let picName = this.checkEndingComma(
        data.ApplicationPicDetail.map((item) => item.picName).join(",")
      );
      let picEmail = this.checkEndingComma(
        data.ApplicationPicDetail.map((item) => item.picEmail).join(",")
      );
      let picContactNo = this.checkEndingComma(
        data.ApplicationPicDetail.map((item) => item.picContactNo).join(",")
      );

      this.incomingData = {
        companySsmNo: [data.Company[0].oldSsmNo],
        companyTinNo: [data.Company[0].companyTinNo],
        companyName: [data.Company[0].oldCompanyName],
        companyAddress: [data.Company[0].oldAddress],
        picName: [picName],
        picEmail: [picEmail],
        picContactNo: [picContactNo],
        bankName: [data.bankUuid],
        bankAccount: [data.oldBankAccount],
        bankAccountHolderName: [data.oldBankAccountHolderName],
        customerService: [
          data.ApplicationCustomerService.filter(
            (item) => item.csName && item.csName !== ""
          )
            .map((item) => item.csName)
            .join(","),
        ],
        totalPrice: [data.totalPrice],
        paymentAmount: [data.paymentAmount],
        programmeName: [data.programmeName],
        referByName: [data.referByName],
        referByEmail: [data.referByEmail],
        noOfPax: [data.noOfPax],
        participantName: [
          data.ApplicationEventParticipant.map(
            (item) => item.participantName
          ).join(","),
        ],
        participantNric: [
          data.ApplicationEventParticipant.map(
            (item) => item.participantNric
          ).join(","),
        ],
        participantEmail: [
          data.ApplicationEventParticipant.map(
            (item) => item.participantEmail
          ).join(","),
        ],
        paymentChannel: [data.paymentChannel],
        paymentMode: [data.paymentMode],
        isApplicationDuplicate: [data.isApplicationDuplicate],
        eventUuid: [data.eventUuid],
      };
    },
    editApplication() {
      this.loadingBar = !this.loadingBar;

      if (this.isArrayofObjects(this.csDetails.customerService)) {
        this.csDetails.customerService = this.csDetails.customerService.map(
          (item) => item.value
        );
      }

      // Clear bank & screenshot if Claim Basis.
      if (this.applicationDetails.paymentChannel == 2) {
        this.companyDetails.bankUuid = null;
        this.companyDetails.bankAccNo = null;
        this.companyDetails.bankAccHolderName = null;
      }

      const fd = new FormData();
      fd.append("userUuid", this.auth.uuid);
      fd.append("applicationUuid", this.$route.query._ausk);
      fd.append("companyUuid", this.companyDetails.companyUuid);
      fd.append("companySsmNo", this.companyDetails.companySsmNo);
      fd.append("companyTinNo", this.companyDetails.companyTinNo);
      fd.append("companyName", this.companyDetails.companyName);
      fd.append("companyAddress", this.companyDetails.companyAddress);
      fd.append("picName", this.companyDetails.picName.join(","));
      fd.append("picEmail", this.companyDetails.picEmail.join(","));
      fd.append("picContactNo", this.companyDetails.picContactNo.join(","));
      fd.append("bankUuid", this.companyDetails.bankUuid);
      fd.append("bankAccNo", this.companyDetails.bankAccNo);
      fd.append("bankAccHolderName", this.companyDetails.bankAccHolderName);
      fd.append("customerService", this.csDetails.customerService);
      fd.append("totalPrice", this.applicationDetails.totalPrice);
      fd.append("paymentAmount", this.applicationDetails.paymentAmount);
      fd.append("paymentChannel", this.applicationDetails.paymentChannel);
      fd.append("ssOfBankAcc", this.applicationDetails.ssOfBankAcc);
      fd.append("programmeName", this.applicationDetails.programmeName);
      fd.append("referByName", this.applicationDetails.referByName);
      fd.append("referByEmail", this.applicationDetails.referByEmail);
      fd.append("noOfPax", this.applicationDetails.noOfPax);
      fd.append(
        "participantName",
        this.applicationDetails.participantName
          .filter((name) => name !== "")
          .slice(0, this.applicationDetails.noOfPax)
      );
      fd.append(
        "participantNric",
        this.applicationDetails.participantNric
          .filter((nric) => nric !== "")
          .slice(0, this.applicationDetails.noOfPax)
      );
      fd.append(
        "participantEmail",
        this.applicationDetails.participantEmail
          .filter((email) => email !== "")
          .slice(0, this.applicationDetails.noOfPax)
      );

      let paymentMode = this.applicationDetails.paymentMode;
      if (paymentMode == "Other") {
        paymentMode = this.applicationDetails.otherPaymentMode;
      }

      fd.append("paymentMode", paymentMode);
      fd.append(
        "isApplicationDuplicate",
        this.applicationDetails.isApplicationDuplicate
      );
      fd.append("eventUuid", this.applicationDetails.eventUuid);
      fd.append("incomingData", JSON.stringify(this.incomingData));

      this.apiEditApplication.setParams(fd);
      this.apiEditApplication.setCallbackCompleted((response) => {
        try {
          this.loadingBar = !this.loadingBar;
          this.clearErrorMsg();
          if (!response.status) {
            this.showErrorMessage(response);
            this.$store.dispatch("showMessage", {
              message: "Please fill-in all the fields",
              messageType: "error",
              timeout: 2000,
            });
          }

          if (response.status) {
            let route = this.eventRoute("Application");
            if (this.params.backTo) {
              route = this.params.backTo;
            }

            this.$store.commit("assignDeveloperData", {
              paginationPageNo: this.$route.query.currentPageNo,
            });

            this.$router.push({
              name: route,
              params: {
                modelKey: this.model.key,
                backTo: "",
              },
              query: {
                viewId: this.$store.state[this.service.key].data.viewId,
                _ausk: this.$route.query._ausk,
              },
            });

            this.$store.dispatch("showMessage", {
              message: response.data,
              messageType: "success",
              timeout: 2000,
            });
            return;
          }
          this.loadingBar = !this.loadingBar;
        } catch (err) {
          console.log(err);
        }
      });
      this.apiEditApplication.fetch();

      this.loadingBar = !this.loadingBar;
      this.alertEditApplicationDialog = !this.alertEditApplicationDialog;
    },
  },
};
</script>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .edit-page {
  background-color: var(--app-page);
}

.dark-mode .edit-header {
  background: var(--app-elevated);
}

.dark-mode .back-btn {
  background: var(--app-card);
  border: 1px solid var(--app-border-strong);
}

.dark-mode .back-btn:hover {
  background: var(--app-surface);
  border-color: var(--app-border-strong);
}

.dark-mode .hero-action-btn {
  background: var(--app-elevated);
  color: var(--app-text-muted);
}

.dark-mode .hero-action-btn:hover {
  background: var(--app-elevated);
  color: var(--app-text);
}

.dark-mode .hero-action-btn--dark {
  background: var(--app-elevated);
  color: var(--app-text);
}

.dark-mode .hero-action-btn--dark:hover {
  background: var(--app-elevated);
  color: var(--app-text);
}

.dark-mode .section-card {
  background: var(--app-elevated);
}

.dark-mode .section-body {
  background: var(--app-card);
}

.dark-mode .form-field >>> .v-text-field--filled:not(.v-input--is-disabled) > .v-input__control > .v-input__slot {
  background: var(--app-card) !important;
  border: 1.5px solid var(--app-border-strong) !important;
}

.dark-mode .form-field >>> .v-text-field--filled:not(.v-input--is-disabled) > .v-input__control > .v-input__slot:hover {
  border-color: var(--app-border-strong) !important;
}

.dark-mode .form-field >>> .v-input--is-disabled .v-input__slot {
  background: var(--app-elevated) !important;
  border: 1px dashed var(--app-border) !important;
}

.dark-mode .form-field >>> .v-input--is-disabled input,
.dark-mode .form-field >>> .v-input--is-disabled textarea,
.dark-mode .form-field >>> .v-input--is-disabled .v-select__selection {
  color: var(--app-text-faint) !important;
}

.dark-mode .form-field >>> .v-label {
  color: var(--app-text-muted);
}

.dark-mode .edit-confirm-card {
  background: var(--app-card);
}

.dark-mode .edit-confirm-card__title {
  color: var(--app-text);
}

.dark-mode .edit-confirm-card__message {
  color: var(--app-text-muted);
}

.dark-mode .edit-confirm-card__btn--cancel {
  background: var(--app-card);
  border: 1.5px solid var(--app-border-strong);
}

.dark-mode .edit-confirm-card__btn--cancel:hover {
  background: var(--app-surface);
}

.dark-mode .skel-header {
  background: var(--app-elevated);
}

.dark-mode .skel-line {
  background: linear-gradient(90deg, var(--app-elevated) 25%, var(--app-elevated) 50%, var(--app-elevated) 75%);
}

.dark-mode .skel-card {
  background: var(--app-elevated);
}

/* >>> end auto-dark <<< */

/* ============================================
   PAGE WRAPPER
   ============================================ */
.edit-page {
  background-color: #d8dce2;
  /* Fills main minus the breadcrumb and footer; a `100vh - 64px` guess only
     subtracts the app bar and overflows. See .layout-main__content. */
  flex: 1 0 auto;
  padding-top: 24px !important;
  padding-bottom: 60px !important;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
  zoom: 1.1;
}

/* ============================================
   EDIT HEADER
   ============================================ */
.edit-header {
  background: #eaecef;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  margin-bottom: 24px;
  overflow: hidden;
}

.edit-header__top {
  display: flex;
  align-items: center;
  padding: 24px 28px 16px;
}

.back-btn {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1px solid #d1d5db;
  cursor: pointer;
  margin-right: 16px;
  transition: all 0.2s ease;
}

.back-btn:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.edit-header__info {
  flex: 1;
  min-width: 0;
}

.edit-header__title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #3a3f47;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  letter-spacing: -0.2px;
}

.edit-header__actions {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-left: 16px;
}

/* Hero action buttons */
.hero-action-btn {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}

.hero-action-btn:hover {
  background: #e2e8f0;
  color: #334155;
  transform: translateY(-1px);
}

.hero-icon {
  width: 18px;
  height: 18px;
}

.hero-action-btn--dark {
  background: #f1f5f9;
  color: #1e293b;
}

.hero-action-btn--dark:hover {
  background: #e2e8f0;
  color: #0f172a;
}

/* ============================================
   CARDS GRID LAYOUT
   ============================================ */
.cards-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
}

@media (min-width: 960px) {
  .cards-grid {
    grid-template-columns: 1fr 1fr;
  }
}

.cards-column {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* ============================================
   SECTION CARDS
   ============================================ */
.section-card {
  background: #eaecef;
  border-radius: 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  overflow: hidden;
  transition: box-shadow 0.3s ease;
}

.section-card:hover {
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.06);
}

.section-header {
  background: linear-gradient(135deg, #0ea5e9, #38bdf8);
  padding: 13px 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.section-header__title {
  color: #ffffff;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
}

.section-body {
  padding: 22px;
  background: #fff;
}

.section-divider {
  border: none;
  border-top: 1px solid #eceef1;
  margin: 12px 0;
}

/* ============================================
   FORM ROWS & FIELDS
   ============================================ */
.form-row {
  display: flex;
  align-items: flex-start;
  margin-bottom: 12px;
  gap: 12px;
}

.form-label {
  flex: 0 0 180px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #2d3239;
  line-height: 1.6;
  padding-top: 10px;
}

.form-field {
  flex: 1;
  min-width: 0;
}

.form-field--participants {
  display: flex;
  gap: 8px;
}

.participant-input {
  flex: 1;
  min-width: 0;
}

/* Style Vuetify inputs to match design system */
.form-field >>> .v-input {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

.form-field >>> .v-input__slot {
  border-radius: 10px !important;
  min-height: 40px !important;
}

.form-field >>> .v-text-field__details {
  margin-bottom: 0 !important;
  padding-top: 2px !important;
  min-height: 0 !important;
}

.form-field >>> .v-messages {
  min-height: 0 !important;
}

.form-field >>> .v-text-field--filled > .v-input__control > .v-input__slot {
  border-radius: 10px !important;
}

.form-field >>> .v-input--is-focused .v-input__slot {
  border-color: #56bde8 !important;
  box-shadow: 0 0 0 3px rgba(86, 189, 232, 0.12) !important;
}

.form-field >>> .v-input__slot::before,
.form-field >>> .v-input__slot::after {
  display: none !important;
}

/* Editable fields - white background with visible border */
.form-field >>> .v-text-field--filled:not(.v-input--is-disabled) > .v-input__control > .v-input__slot {
  background: #fff !important;
  border: 1.5px solid #d1d5db !important;
}

.form-field >>> .v-text-field--filled:not(.v-input--is-disabled) > .v-input__control > .v-input__slot:hover {
  border-color: #94a3b8 !important;
}

/* Disabled / read-only fields - flat muted look */
.form-field >>> .v-input--is-disabled .v-input__slot {
  background: #f1f5f9 !important;
  border: 1px dashed #e2e8f0 !important;
  cursor: not-allowed !important;
}

.form-field >>> .v-input--is-disabled input,
.form-field >>> .v-input--is-disabled textarea,
.form-field >>> .v-input--is-disabled .v-select__selection {
  color: #94a3b8 !important;
}

.form-field >>> .v-text-field--filled:not(.v-input--is-focused) > .v-input__control > .v-input__slot::before {
  border: none !important;
}

.form-field >>> .v-text-field--filled > .v-input__control > .v-input__slot::after {
  border: none !important;
}

.form-field >>> .v-label {
  font-family: 'Lato', sans-serif;
  font-size: 0.85rem;
  color: #64748b;
}

.form-field >>> .v-text-field input,
.form-field >>> .v-select__selection,
.form-field >>> .v-text-field textarea {
  font-family: 'Lato', sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  color: #1a1e24;
}

/* Error state */
.form-field >>> .v-input--has-state .v-input__slot {
  border-color: #f87171 !important;
}

.form-field >>> .v-text-field--filled:not(.v-input--is-disabled).v-input--has-state > .v-input__control > .v-input__slot {
  border-color: #ef4444 !important;
}

.form-field >>> .v-messages__message {
  font-family: 'Lato', sans-serif;
  font-size: 0.75rem;
  color: #ef4444;
}

/* ============================================
   EDIT CONFIRM DIALOG
   ============================================ */
.edit-confirm-dialog {
  border-radius: 20px !important;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12) !important;
}

.edit-confirm-card {
  background: #fff;
  border-radius: 20px;
  padding: 32px 32px 24px;
  text-align: center;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
}

.edit-confirm-card__title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 10px;
}

.edit-confirm-card__message {
  font-size: 0.9rem;
  color: #64748b;
  margin: 0 0 24px;
  line-height: 1.5;
}

.edit-confirm-card__actions {
  display: flex;
  gap: 12px;
}

.edit-confirm-card__btn {
  flex: 1;
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  font-family: 'Lato', sans-serif;
  cursor: pointer;
  transition: all 0.15s ease;
}

.edit-confirm-card__btn--cancel {
  background: #fff;
  color: #0ea5e9;
  border: 1.5px solid #d1d5db;
}

.edit-confirm-card__btn--cancel:hover {
  background: #f8fafc;
  border-color: #0ea5e9;
}

.edit-confirm-card__btn--confirm {
  background: #56bde8;
  color: #fff;
  border: none;
}

.edit-confirm-card__btn--confirm:hover {
  background: #3dadd8;
  box-shadow: 0 4px 12px rgba(86, 189, 232, 0.3);
}

/* ============================================
   PAGE LOADER / SKELETON
   ============================================ */
.page-loader {
  padding: 0;
}

.skel-header {
  background: #eaecef;
  border-radius: 14px;
  padding: 28px;
  margin-bottom: 24px;
}

.skel-line {
  height: 12px;
  border-radius: 6px;
  background: linear-gradient(90deg, #dfe1e5 25%, #e8eaed 50%, #dfe1e5 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  margin-bottom: 10px;
}

.skel-line--sm { width: 30%; }
.skel-line--md { width: 50%; }
.skel-line--lg { width: 70%; height: 14px; }
.skel-line--full { width: 100%; }

.skel-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.skel-card {
  background: #eaecef;
  border-radius: 14px;
  overflow: hidden;
}

.skel-card__header {
  height: 42px;
  background: linear-gradient(90deg, #cdd1d6 25%, #d8dce0 50%, #cdd1d6 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.skel-card__body {
  padding: 20px;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ============================================
   MOBILE RESPONSIVE
   ============================================ */
@media (max-width: 599px) {
  .edit-page {
    padding: 12px 8px !important;
    zoom: 1;
  }

  .edit-header {
    border-radius: 14px;
    margin-bottom: 16px;
  }

  .edit-header__top {
    flex-wrap: wrap;
    padding: 16px 16px 12px;
  }

  .back-btn {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    margin-right: 12px;
  }

  .edit-header__title {
    font-size: 1.05rem;
    white-space: normal;
  }

  .edit-header__actions {
    width: 100%;
    margin-left: 0;
    margin-top: 10px;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 4px;
  }

  /* Hero action buttons - usable size on mobile */
  .hero-action-btn {
    width: 36px;
    height: 36px;
  }

  /* Grid stacks to single column */
  .cards-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .cards-column {
    gap: 16px;
  }

  .section-card {
    border-radius: 14px;
  }

  .section-header {
    padding: 12px 16px;
  }

  .section-header__title {
    font-size: 0.65rem;
    letter-spacing: 1.5px;
  }

  .section-body {
    padding: 14px;
  }

  .form-row {
    flex-direction: column;
    gap: 2px;
    align-items: stretch;
  }

  .form-label {
    flex: none;
    width: 100%;
    font-size: 0.72rem;
    padding-top: 0;
    margin-bottom: 2px;
  }

  .form-field {
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box;
  }

  .form-field--participants {
    flex-direction: column;
    gap: 4px;
  }

  /* Vuetify inputs full width */
  .form-field >>> .v-input {
    width: 100% !important;
  }

  .form-field >>> .v-input__control {
    width: 100% !important;
  }

  .form-field >>> .v-input__slot {
    width: 100% !important;
    box-sizing: border-box !important;
  }

  /* Section body overflow */
  .section-body {
    overflow: hidden;
    word-break: break-word;
  }

  .section-card {
    max-width: 100%;
    overflow: hidden;
  }

  .cards-column {
    min-width: 0;
    max-width: 100%;
  }

  /* Prevent page horizontal scroll */
  .edit-page {
    overflow-x: hidden;
  }

  /* Dialog smaller padding on mobile */
  .edit-confirm-card {
    padding: 24px 20px 20px;
  }

  .edit-confirm-card__title {
    font-size: 1.05rem;
  }

  .skel-grid {
    grid-template-columns: 1fr;
  }
}

/* Tablet breakpoint */
@media (min-width: 600px) and (max-width: 959px) {
  .edit-page {
    zoom: 1;
  }

  .edit-header__top {
    padding: 20px 20px 14px;
  }

  .edit-header__title {
    font-size: 1.15rem;
  }

  .edit-header__actions {
    flex-wrap: wrap;
    gap: 4px;
  }

  .cards-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .section-body {
    padding: 18px;
  }

  .form-label {
    flex: 0 0 160px;
  }
}

/* ============================================
   DARK MODE
   ============================================ */
.dark-mode .edit-page {
  background-color: var(--app-page);
}

.dark-mode .edit-header {
  background: var(--app-page);
}

.dark-mode .edit-header__title {
  color: var(--app-text);
}

.dark-mode .back-btn {
  background: var(--app-page);
  border-color: var(--app-border);
}

.dark-mode .back-btn:hover {
  background: var(--app-card);
  border-color: var(--app-border-strong);
}

.dark-mode .section-card {
  background: var(--app-page);
}

/* --- Section headers — the neutral dark band, same treatment as
       hrdc/views/application/View.vue.

       The light theme paints these with a saturated sky gradient
       (#0ea5e9 -> #38bdf8); dark used to carry that over as a flat, undiluted
       --app-accent bar, and a column of those on charcoal is the glare this
       page was reported for. The band is now neutral — var(--app-card),
       one step above the --app-page body it caps, closed by --app-border and lifted by the same
       `inset 0 1px 0` highlight every other raised surface uses — so it reads
       as a header by GEOMETRY rather than by hue. The accent survives only at
       sizes small enough to be a cue instead of a field: a 2px spine that fades
       out down the band, and the icon glyph. */
.dark-mode .section-header {
  position: relative;
  background-color: var(--app-card);
  background-image: none;
  border-bottom: 1px solid var(--app-border);
  box-shadow: inset 0 1px 0 var(--app-inner-highlight);
}

/* What is left of the accent: 2px wide and faded top-to-bottom, so it marks
   the header's start without drawing a hard full-height rail.
   .section-card's overflow clips it to the card radius. */
.dark-mode .section-header::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 2px;
  background: linear-gradient(
    180deg,
    rgba(var(--app-accent-rgb), 0.55),
    rgba(var(--app-accent-rgb), 0.12)
  );
}

.dark-mode .section-header__title {
  color: var(--app-text);
  font-weight: 700;
  letter-spacing: 1.6px;
}

/* The icon can hold the accent now that the band behind it is neutral.
   `color="white"` on the tag compiles to .white--text (0,1,0 + !important);
   this is (0,3,0 + !important) and wins. */
.dark-mode .section-header .v-icon {
  color: var(--app-accent) !important;
  opacity: 0.85;
}

.dark-mode .section-body {
  background: var(--app-page);
}

.dark-mode .section-divider {
  border-top-color: var(--app-border);
}

.dark-mode .form-label {
  color: var(--app-text-muted);
}

.dark-mode .form-field >>> .v-text-field--filled > .v-input__control > .v-input__slot {
  border-color: var(--app-border) !important;
}

.dark-mode .form-field >>> .v-text-field--filled > .v-input__control > .v-input__slot:hover {
  background: var(--app-elevated) !important;
  border-color: var(--app-border-strong) !important;
}

.dark-mode .form-field >>> .v-input--is-focused .v-input__slot {
  border-color: var(--app-accent) !important;
  box-shadow: 0 0 0 3px var(--app-ring) !important;
}

.dark-mode .form-field >>> .v-text-field input,
.dark-mode .form-field >>> .v-select__selection,
.dark-mode .form-field >>> .v-text-field textarea {
  color: var(--app-text);
}

.dark-mode .form-field >>> .v-label {
  color: var(--app-text-faint);
}

.dark-mode .form-field >>> .v-text-field--filled:not(.v-input--is-disabled) > .v-input__control > .v-input__slot {
  background: var(--app-page) !important;
  border: 1.5px solid var(--app-border) !important;
}

.dark-mode .form-field >>> .v-text-field--filled:not(.v-input--is-disabled) > .v-input__control > .v-input__slot:hover {
  border-color: var(--app-border-strong) !important;
}

.dark-mode .form-field >>> .v-input--is-disabled .v-input__slot {
  background: var(--app-page) !important;
  border: 1px dashed var(--app-border) !important;
}

.dark-mode .form-field >>> .v-input--is-disabled input,
.dark-mode .form-field >>> .v-input--is-disabled textarea,
.dark-mode .form-field >>> .v-input--is-disabled .v-select__selection {
  color: var(--app-text-faint) !important;
}

.dark-mode .hero-action-btn {
  background: var(--app-card);
  color: var(--app-text-muted);
}

.dark-mode .hero-action-btn:hover {
  background: var(--app-elevated);
  color: var(--app-neutral-fg);
}

.dark-mode .hero-action-btn--dark {
  background: var(--app-border);
  color: var(--app-text);
}

.dark-mode .hero-action-btn--dark:hover {
  background: var(--app-border-strong);
  color: var(--app-text);
}

.dark-mode .edit-confirm-card {
  background: var(--app-page);
}

.dark-mode .edit-confirm-card__title {
  color: var(--app-text);
}

.dark-mode .edit-confirm-card__message {
  color: var(--app-text-muted);
}

.dark-mode .edit-confirm-card__btn--cancel {
  background: var(--app-page);
  color: var(--app-accent-strong);
  border-color: var(--app-border);
}

.dark-mode .edit-confirm-card__btn--cancel:hover {
  background: var(--app-card);
  border-color: var(--app-accent-strong);
}

.dark-mode .edit-confirm-card__btn--confirm {
  background: var(--app-accent);
}

.dark-mode .edit-confirm-card__btn--confirm:hover {
  background: var(--app-accent);
}

.dark-mode .skel-header {
  background: var(--app-page);
}

.dark-mode .skel-line {
  background: linear-gradient(90deg, var(--app-card) 25%, var(--app-elevated) 50%, var(--app-card) 75%);
  background-size: 200% 100%;
}

.dark-mode .skel-card {
  background: var(--app-page);
}

.dark-mode .skel-card__header {
  background: linear-gradient(90deg, var(--app-elevated) 25%, var(--app-border) 50%, var(--app-elevated) 75%);
  background-size: 200% 100%;
}

/* ============================================
   VUETIFY OVERRIDES
   ============================================ */
.v-btn {
  opacity: 1 !important;
}

.v-messages__message {
  color: #ef4444;
}


/* >>> dark companions: the light rules above pin these to a dark ink
   with !important, which on the dark field renders the text invisible
   rather than absent. Token references only. <<< */

.dark-mode .form-field >>> .v-text-field input,
.dark-mode .form-field >>> .v-select__selection,
.dark-mode .form-field >>> .v-text-field textarea {
  color: var(--app-text) !important;
}
</style>
